import { z } from "zod";

const NameOnlySchema = z.object({
    name: z.string().nullable()
});

const ContactSchema = z.object({
    name: z.string().nullable(),
    phone: z.string().nullable(),
    mobile: z.string().nullable(),
    workPhone: z.string().nullable(),
    email: z.string().nullable(),
});

const AddressSchema = z.object({
    street1: z.string().nullable(),
    street2: z.string().nullable(),
    suburb: z.string().nullable(),
    state: z.string().nullable(),
    postcode: z.union([z.string(), z.number()]).nullable(),
});

const CodeItemSchema = z.object({
    name: z.string(),
    location: z.string().nullable(),
    quantity: z.number().default(0),
    note: z.string().nullable(),
    status: z.string().nullable(),
    integrationId: z.string().nullable(),
});

export const JobCreateSchema = z.object({
    type: NameOnlySchema.describe("High-level classification that defines the nature or category of work being requested in a work order"),
    startDate: z.string().nullable().describe("Start date is the date the work is scheduled to begin. Please provide it in the format YYYY-MM-DD HH:mm."),
    dueDate: z.string().nullable().describe("Please provide it in the format YYYY-MM-DD HH:mm."),
    description: z.string().describe("Include the entire description outling the details of the job, including any site access information required."),
    contact: ContactSchema.describe("This should be filled out with contact info related to the tenant, or the owner/resident of the address"),
    address: AddressSchema,
    priority: NameOnlySchema,
    codes: z.array(CodeItemSchema).describe("Codes are any work or items that are needed for the job. They usually have a quantity, code(name), location, and note."),
    customerWO: z.string().describe("A unique identifier for the work order that is in every file. e.g. 'WO-XXXX', 'WO3718', etc."),
    placeId: z.string().nullable(),
    integrationId: z.string().nullable(),
    supervisor: NameOnlySchema.describe("the client’s designated contact for this work order, who oversees the job, provides instructions/approvals, and signs off on completion."),
    site: NameOnlySchema.describe("the name of the site where the work is being done"),
    claimToken: z.string().nullable(),
    labels: z.array(z.string()).default([]),
});

export type JobCreateSchemaType = typeof JobCreateSchema;


export type WorkOrderImport = {
    workOrderNumber: string;
    generatedOn?: string; // Optional, as not all work orders might have this field
    createdDate?: string;
    startDate?: string;
    dueDate?: string;
    jobTitle: string;
    jobDescription: string;
    jobType: string;
    priority: string;
    property: {
        address: {
            street1: string;
            street2: string;
            suburb: string;
            state: string;
            postcode: string;
            country: string;
        };
        key?: string;
        notes?: string;
    };
    agency: {
        name: string;
        contact: {
            name: string;
            phone: string;
            email: string;
        };
    };
    tenant: {
        primaryTenant: {
            name: string;
            mobile: string;
            homePhone: string;
            phone: string;
            email: string;
        };
        additionalTenants?: {
            name: string;
            mobile: string;
            homePhone: string;
            phone: string;
            email: string;
        }[];
    };
    serviceProvider: {
        name: string;
        contact: {
            name: string;
            phone: string;
            email: string;
        };
    };
    codes?: {
        code: string;
        note: string;
        location: string;
        quantity: string;
    }[];
};